import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  email as emailSchema,
  insurable,
  insuranceHolder as insuranceHolderSchema,
  insurancePremiumCents,
  teamName as teamNameSchema,
  voucherErrorReason,
  type InsuranceHolder,
} from '@gg/shared';
import {
  Button,
  CheckboxField,
  CheckIcon,
  Notice,
  Recap,
  RecapRow,
  StepHeader,
  StickyFooter,
  TextField,
  UsersIcon,
} from '@/components/ui';
import { InsuranceCard } from '@/components/InsuranceCard';
import { Turnstile } from '@/components/Turnstile';
import { VoucherField } from '@/components/VoucherField';
import { ApiRequestError } from '@/lib/api';
import { funnel } from '@/lib/analytics';
import { draftKey, loadDraft, reusablePending, saveDraft, savePending } from '@/lib/checkoutDraft';
import { formatDay } from '@/lib/dates';
import { isPurchasable, isTeamFormat, localName, teamSizeLabel } from '@/lib/formats';
import { formatPrice } from '@/lib/i18n';
import { saveOrderClaim } from '@/lib/orderClaim';
import { useCreateCheckout, useEvent, useMe, useVoucherPreview } from '@/lib/queries';
import { storedReferralCode } from '@/lib/referral';

const formSchema = z.object({
  email: emailSchema,
  acceptTerms: z.literal(true),
  marketingOptIn: z.boolean(),
  /** Checked only when a team format is chosen (see `teamFormSchema`). */
  teamName: z.string(),
});
const teamFormSchema = formSchema.extend({ teamName: teamNameSchema });
type FormValues = z.infer<typeof formSchema>;

/** Reason of a `voucher_invalid` API error, if that is what `err` is. */
function voucherReason(err: unknown) {
  if (!(err instanceof ApiRequestError) || err.code !== 'voucher_invalid') return null;
  const parsed = voucherErrorReason.safeParse((err.details as { reason?: unknown })?.reason);
  return parsed.success ? parsed.data : 'invalid';
}

/** API error codes with their own message; anything else shows the generic error. */
const KNOWN_ERRORS = [
  'event_closed',
  'not_on_sale',
  'not_found',
  'captcha_failed',
  'payment_unavailable',
  'insurance_unavailable',
  'already_registered',
  'team_name_required',
  'team_format_alone',
  'team_format_mismatch',
  'link_invalid',
  'already_joined',
  'team_cancelled',
  'team_full',
  'sold_out',
];

/**
 * Screen 02: email + consent (P-02). On submit → POST /checkout → /pay with the
 * PaymentIntent. Only email, terms and an optional opt-in before paying (invariant 7).
 * Screen 02b when adding a race from the profile (P-13): logged in, so no email field and
 * no consent boxes (terms accepted with the first order).
 */
export function Register() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const expired = (useLocation().state as { expired?: boolean } | null)?.expired;
  const { data: event } = useEvent();
  const checkout = useCreateCheckout();
  const [draft] = useState(loadDraft);
  const me = useMe();
  // 02b only with a live session; otherwise the normal email step.
  const account = draft.upsell && me.data ? me.data : null;
  // P-21: a teammate paying their place from the invitation link (team context from /teams/join).
  const joinCode = draft.joinCode;
  const teamCtx = joinCode ? draft.team : undefined;
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(0);
  const onToken = useCallback((tok: string) => setTurnstileToken(tok), []);

  const chosen = (event?.formats ?? []).filter(
    (f) => draft.formatIds.includes(f.id) && isPurchasable(f),
  );
  const chosenIds = chosen.map((f) => f.id);
  // P-20: a team format is the order's only format and, for its captain, needs a team name.
  const teamFormat = chosen.find(isTeamFormat) ?? null;
  const namesTeam = teamFormat !== null && !teamCtx;
  // A teammate's place is priced at the team's locked tier, not the tier on sale (record 0021).
  const priceOf = (f: { id: string; currentTier: { priceCents: number } | null }) =>
    teamCtx && f.id === teamCtx.formatId ? teamCtx.priceCents : (f.currentTier?.priceCents ?? 0);
  const subtotal = chosen.reduce((s, f) => s + priceOf(f), 0);

  // P-03: the applied code is re-checked whenever the selection changes or the page reloads.
  const [voucherCode, setVoucherCode] = useState(draft.voucherCode);
  const voucher = useVoucherPreview(event?.id, voucherCode, chosenIds, joinCode);
  const appliedVoucher = voucher.data ? voucherCode : undefined;
  useEffect(() => {
    if (voucher.isSuccess) saveDraft({ voucherCode });
    // A refused code is not kept: the checkout must not fail on it later.
    if (voucher.isError) saveDraft({ voucherCode: undefined });
  }, [voucher.isSuccess, voucher.isError, voucherCode]);

  // P-19: one insurance for the whole basket, a rate of the formats after the voucher
  // (decision 0025). Hidden when the event doesn't offer it or the formats are free. The
  // server recomputes the premium it charges (invariant 2).
  const formatsTotal = voucher.data?.totalCents ?? subtotal;
  const insurance = event?.insurance && insurable(formatsTotal) ? event.insurance : null;
  const [insuranceOn, setInsuranceOn] = useState(draft.insurance);
  const insured = insurance !== null && insuranceOn;
  const premium = insured ? insurancePremiumCents(formatsTotal, insurance.rateBps) : 0;
  const total = formatsTotal + premium;
  // The insured person's name, asked only with the insurance (decision 0025 D1).
  const [holder, setHolder] = useState<InsuranceHolder>(
    draft.insuranceHolder ?? { firstName: '', lastName: '' },
  );
  const [holderErrors, setHolderErrors] = useState<{ firstName?: boolean; lastName?: boolean }>();

  const form = useForm<FormValues>({
    resolver: zodResolver(namesTeam ? teamFormSchema : formSchema),
    mode: 'onTouched',
    defaultValues: {
      email: draft.email,
      acceptTerms: draft.acceptTerms as true,
      marketingOptIn: draft.marketingOptIn,
      teamName: draft.teamName ?? '',
    },
  });

  // Every keystroke and tick survives a reload (invariant 7).
  useEffect(() => {
    const sub = form.watch((v) =>
      saveDraft({
        email: v.email ?? '',
        acceptTerms: v.acceptTerms === true,
        marketingOptIn: v.marketingOptIn === true,
        teamName: v.teamName || undefined,
      }),
    );
    return () => sub.unsubscribe();
  }, [form]);

  if (draft.formatIds.length === 0) return <Navigate to="/formats" replace />;

  const { errors } = form.formState;

  /** The insured person's name, or null (and the fields flagged) when it is missing. */
  const checkedHolder = (): InsuranceHolder | null => {
    const parsed = insuranceHolderSchema.safeParse(holder);
    if (parsed.success) return parsed.data;
    setHolderErrors({ firstName: !holder.firstName.trim(), lastName: !holder.lastName.trim() });
    return null;
  };

  const submitCheckout = async (values: FormValues, insuranceHolder: InsuranceHolder | null) => {
    if (!event || chosen.length === 0) return;
    const current = saveDraft({
      ...values,
      formatIds: chosenIds,
      voucherCode: appliedVoucher,
      insurance: insured,
      insuranceHolder: insuranceHolder ?? undefined,
      teamName: namesTeam ? values.teamName.trim() : undefined,
    });

    // Back from /pay without changes: pay the same order instead of holding a second one.
    if (reusablePending(current)) {
      navigate('/pay');
      return;
    }

    funnel.beginCheckout(total);
    try {
      const out = await checkout.mutateAsync({
        eventId: event.id,
        email: values.email,
        formatIds: current.formatIds,
        acceptTerms: true,
        marketingOptIn: values.marketingOptIn,
        voucherCode: current.voucherCode,
        insurance: current.insurance,
        insuranceHolder: current.insurance ? insuranceHolder! : undefined,
        teamName: current.teamName,
        joinCode,
        // Link of this visit first, else one from an earlier visit (kept 30 days).
        referralCode: current.referralCode ?? storedReferralCode(),
        locale: i18n.language === 'en' ? 'en' : 'fr',
        turnstileToken,
      });
      // Lets the confirmation screen open the session once the order is paid (P-07).
      saveOrderClaim(out.orderId, out.claimToken);
      // Free order (0 €): already confirmed by the API, no payment step (decision 0004 D5).
      if (out.clientSecret === null) {
        navigate(`/success?order=${out.orderId}`);
        return;
      }
      savePending({ ...out, email: values.email, key: draftKey(current) });
      navigate('/pay');
    } catch (err) {
      // The code was valid on preview but not any more (e.g. last use taken): show why.
      if (voucherReason(err)) void voucher.refetch();
      // Turnstile tokens are single-use: get a fresh one for the retry.
      setTurnstileToken('');
      setTurnstileKey((k) => k + 1);
    }
  };
  const onSubmit = account
    ? (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const insuranceHolder = insured ? checkedHolder() : null;
        if (namesTeam && !teamNameSchema.safeParse(form.getValues('teamName')).success) {
          form.setError('teamName', { type: 'invalid' });
          return;
        }
        if (insured && !insuranceHolder) return;
        void submitCheckout(
          {
            email: account.email,
            acceptTerms: true,
            marketingOptIn: false,
            teamName: form.getValues('teamName'),
          },
          insuranceHolder,
        );
      }
    : (e: FormEvent<HTMLFormElement>) => {
        const insuranceHolder = insured ? checkedHolder() : null;
        void form.handleSubmit((values) => {
          if (insured && !insuranceHolder) return;
          return submitCheckout(values, insuranceHolder);
        })(e);
      };

  const errorCode = checkout.error instanceof ApiRequestError ? checkout.error.code : null;
  const voucherError = voucherReason(voucher.error) ?? voucherReason(checkout.error);
  // Terms of sale and race regulations are two documents, each with its own URL.
  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-1 flex-col pt-13">
      <StepHeader
        backTo="/formats"
        backLabel={t('register.back')}
        step={2}
        title={t(account ? 'register.titleAddRace' : 'register.title')}
      />

      <div className="flex flex-col gap-4.5 pt-4.5 pb-2">
        {expired && <Notice>{t('register.expired')}</Notice>}

        {teamCtx && (
          <Notice
            title={t(teamCtx.captain ? 'join.title' : 'join.titleNoCaptain', {
              captain: teamCtx.captain,
              team: teamCtx.name,
            })}
            icon={<UsersIcon size={22} className="text-accent" />}
          >
            {t('join.bannerBody', {
              price: formatPrice(teamCtx.priceCents),
              tier: teamCtx.tierName,
            })}
          </Notice>
        )}

        {chosen.length > 0 && (
          <Recap>
            {chosen.map((f) => (
              <RecapRow
                key={f.id}
                label={
                  <>
                    <span className="font-semibold">{localName(f.name, f.nameEn)}</span>
                    {f.startsAt && (
                      <span className="text-ink-muted"> · {formatDay(f.startsAt, 'short')}</span>
                    )}
                    {isTeamFormat(f) && (
                      <span className="text-ink-muted"> · {teamSizeLabel(t, f)}</span>
                    )}
                  </>
                }
                value={formatPrice(priceOf(f))}
              />
            ))}
          </Recap>
        )}

        {namesTeam && (
          <TextField
            size="lg"
            type="text"
            autoComplete="off"
            maxLength={60}
            label={t('register.teamName')}
            placeholder={t('register.teamNamePlaceholder')}
            hint={t('register.teamNameHint')}
            error={errors.teamName && t('register.teamNameInvalid')}
            {...form.register('teamName')}
          />
        )}

        {account ? (
          <div className="flex items-center justify-between gap-3 text-sm text-ink-muted">
            <span>
              <Trans
                i18nKey="register.loggedInAs"
                values={{ email: account.email }}
                components={{ strong: <span className="font-semibold text-ink" /> }}
              />
            </span>
            <span className="inline-flex shrink-0 items-center gap-1 font-semibold text-success">
              <CheckIcon size={14} />
              {t('register.termsAccepted')}
            </span>
          </div>
        ) : (
          <TextField
            size="lg"
            type="email"
            inputMode="email"
            autoComplete="email"
            label={t('register.email')}
            placeholder={t('register.emailPlaceholder')}
            hint={t('register.emailHint')}
            error={errors.email && t('register.emailInvalid')}
            {...form.register('email')}
          />
        )}

        <VoucherField
          code={voucherCode}
          applied={appliedVoucher}
          preview={voucher.data}
          checking={voucher.isFetching}
          error={voucherError}
          onApply={(code) => {
            if (code === voucherCode) void voucher.refetch();
            else setVoucherCode(code);
          }}
          onRemove={() => {
            setVoucherCode(undefined);
            saveDraft({ voucherCode: undefined });
          }}
        />

        {insurance && (
          <InsuranceCard
            offer={insurance}
            insuredCents={formatsTotal}
            checked={insuranceOn}
            onChange={(on) => {
              setInsuranceOn(on);
              saveDraft({ insurance: on });
            }}
            holder={holder}
            holderErrors={holderErrors}
            onHolderChange={(h) => {
              setHolder(h);
              setHolderErrors(undefined);
              saveDraft({ insuranceHolder: h });
            }}
          />
        )}

        {!account && (
          <div className="flex flex-col gap-3 pt-1">
            <CheckboxField
              aria-required
              error={errors.acceptTerms && t('register.termsRequired')}
              {...form.register('acceptTerms')}
            >
              <Trans
                i18nKey="register.terms"
                components={{
                  rules: (
                    <a href={event?.rulesUrl ?? '/reglement'} target="_blank" rel="noreferrer" />
                  ),
                }}
              />{' '}
              <span aria-hidden="true" className="text-accent">
                *
              </span>
            </CheckboxField>
            <CheckboxField muted {...form.register('marketingOptIn')}>
              {t('register.marketing', { event: event?.name ?? t('app.name') })}
            </CheckboxField>
          </div>
        )}

        <Turnstile key={turnstileKey} onToken={onToken} />

        {checkout.error && errorCode !== 'voucher_invalid' && (
          <p role="alert" className="text-sm text-danger">
            {errorCode && KNOWN_ERRORS.includes(errorCode)
              ? t(`register.error.${errorCode}`)
              : t('error.generic')}
          </p>
        )}
      </div>

      <StickyFooter
        totalLabel={t('register.total')}
        total={formatPrice(total)}
        note={account ? undefined : t('register.finePrint')}
      >
        <Button
          type="submit"
          loading={checkout.isPending}
          disabled={!event || chosen.length === 0 || !turnstileToken || voucher.isFetching}
        >
          {total === 0 ? t('register.submitFree') : t('register.submit')}
        </Button>
      </StickyFooter>
    </form>
  );
}
